import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Button, IconButton, Tooltip, Typography } from '@material-ui/core';
import { sleep } from '@project-serum/common';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router';
import {
  ArrowBack,
  DoubleArrow,
  IndeterminateCheckBoxRounded,
  AddBoxRounded,
} from '@material-ui/icons';

import { useProgramApis } from '../../hooks/useProgramApis';
import { claimPrize as claimPrizeQuery } from '../../lib/actions/claimPrize';
import { Raffle } from '../../lib/types';
import { routes } from '../../router/routes';
import Screen from '../../components/layout/Screen';
import { expand } from '../../lib/randomnessTools';
import EndedRaffleActionSection from './components/EndedRaffleActionsSection';
import RaffleInfoSection from '../../components/RaffleInfoSection';
import PrizeGalleryEnded from './components/PrizeGalleryEnded';
import useCommonStyles from '../../assets/styles';
import { useStyles } from './styles';
import { useViewport } from '../../hooks/useViewport';
import { DeviceType } from '../../providers/ViewportProvider';
import { useWallet } from '@solana/wallet-adapter-react';
import Footer from '../../components/layout/Footer';
import Participants from '../RaffleOngoingScreen/components/Participants';
import moment from 'moment';

interface IRaffleEndedScreenProps {
  raffle: Raffle;
  updateRaffle: () => void;
}

const RaffleEndedScreen: FC<IRaffleEndedScreenProps> = ({
  raffle,
  updateRaffle,
}) => {
  const { publicKey } = useWallet();
  const { device } = useViewport();
  const classes = { ...useCommonStyles(), ...(useStyles({ device }) as any) };
  const { push } = useHistory();
  const { draffleClient } = useProgramApis();
  const [showParticipants, setShowParticipants] = useState(false);

  const prizeGalleryRef = useRef<HTMLDivElement>(null);

  const entrant = useMemo(() => {
    if (!draffleClient.provider.wallet.publicKey) return;

    return raffle?.entrants.get(
      draffleClient.provider.wallet.publicKey.toString()
    );
  }, [raffle, draffleClient.provider.wallet.publicKey]); // "Unnecessary" dependency required due to React not picking up change in publicKey subfield

  // Each winning ticket index for each prize
  const winningTickets = useMemo(() => {
    if (!raffle.randomness || !raffle.entrants || raffle.entrants.size === 0)
      return [];
    const secret = raffle.randomness;
    return raffle.prizes.map((_, prizeIndex) => {
      const rand = expand(secret, prizeIndex);
      return rand % raffle.totalTickets;
    });
  }, [raffle]);

  const claimPrize = useCallback(
    async (prizeIndex: number, ticketIndex: number) => {
      try {
        await claimPrizeQuery(draffleClient, raffle, prizeIndex, ticketIndex);
        await sleep(500);
        updateRaffle();

        toast.success('Prize claimed, check your wallet!');
      } catch (error: any) {
        if (error.msg) {
          toast.error(`Transaction failed: ${error.msg}`);
        } else {
          toast.error('Unexpected error');
        }
      }
    },
    [draffleClient, raffle, updateRaffle]
  );

  const entrantWinningTickets = useMemo(() => {
    if (!entrant || !winningTickets) return [];
    return winningTickets.reduce<{ prizeIndex: number; ticketIndex: number }[]>(
      (acc, ticketIndex, prizeIndex) => {
        if (entrant?.tickets.includes(ticketIndex)) {
          return [...acc, { prizeIndex, ticketIndex }];
        } else {
          return acc;
        }
      },
      []
    );
  }, [entrant, winningTickets]);

  if (!raffle) return null;

  return (
    <div className={classes.root}>
      <div className="relative bg- mt-14 center space-y-2 flex flex-col w-full">
        <div style={{ maxWidth: '800px' }} className="backbutton w-full">
          <button
            onClick={() => push('/')}
            className="GTWalsheimProBold p-2 rounded-md text-brand_secondary bg-brand_smooth_gray"
          >
            Back to Raffles
          </button>
        </div>
        <div
          style={{ maxWidth: '800px' }}
          className="winnersec p-4 space-x-5 items-center bg-brand_smooth_gray rounded-lg w-full flex"
        >
          <div className="staricon bg-brand_smooth_yellow rounded-lg bg-opacity-10 p-2">
            <img src="/image/Shape.png" />
          </div>
          <div className="GTWalsheimProRegula text-brand_secondary text-base flex-1">
            <EndedRaffleActionSection
              raffle={raffle}
              winningTickets={winningTickets}
              userPubkey={draffleClient.provider.wallet.publicKey}
              entrant={entrant}
              entrantWinningTickets={entrantWinningTickets}
              scrollRef={prizeGalleryRef}
              publicKey={publicKey}
              claimPrize={claimPrize}
            />
          </div>
        </div>
        <div
          style={{ maxWidth: '800px' }}
          className="bg-brand_smooth_gray rounded-xl w-full  lg:flex mt- p-4"
        >
          <div className="col1 bg-green-00 w-full lg:w-2/5">
            <div className={classes.prizesSection}>
              <PrizeGalleryEnded
                raffle={raffle}
                entrantWinningTickets={entrantWinningTickets}
                winningTickets={winningTickets}
                claimPrize={claimPrize}
                scrollRef={prizeGalleryRef}
                publicKey={publicKey}
              />
            </div>
          </div>
          <div className="col2 bg-red-00 w-full lg:w-3/5 space-y-3 md:space-y-6 pl-3 pt-16 md:pt-8 lg:pt-0">
            <div className="buttons flex justify-center md:mt-4 lg:mt-0 lg:justify-start space-x-2 bg-red-00 lg:py-4">
              <button
                onClick={() => setShowParticipants(false)}
                className={`${classes.cardButtonOne} primary_font ${
                  showParticipants
                    ? 'bg-brand_primary text-white opacity-30'
                    : 'bg-brand_primary text-white'
                } `}
              >
                Details
              </button>
              <button
                onClick={() => setShowParticipants(true)}
                className={`${classes.cardButtonOne} primary_font ${
                  !showParticipants
                    ? ' bg-brand_primary text-white opacity-30'
                    : 'bg-brand_primary text-white'
                } tracking-wider `}
              >
                Participants
              </button>
            </div>
            <div className="cardname bg-blue-00 pb-3">
              <p className={`${classes.cardTitle} primary_font`}>
                {raffle.prizes[0].meta.name}
              </p>
            </div>
            <div className="pricetext bg-green-00">
              {!showParticipants ? (
                <div className="w-full">
                  <RaffleInfoSection
                    raffle={raffle}
                    userConnected={!!draffleClient.provider.wallet.publicKey}
                    userTickets={entrant?.tickets}
                  />
                  <div className="GTWalsheimProBold endeddate bg-yellow-00 border-b-2 border-dashed border-brand_smooth_yellow text-red-500 pt-6 py-3">
                    Ended {moment(raffle.endTimestamp).fromNow()}
                  </div>
                  <div
                    className={`${classes.cardButtons} flex w-full justify-between`}
                  >
                    <div className="flex items-center">
                      <IndeterminateCheckBoxRounded
                        className="text-white opacity-30 "
                        style={{ fontSize: 30 }}
                      />
                      <p className="text-white opacity-30">0</p>
                      <AddBoxRounded
                        className="text-white opacity-30"
                        style={{ fontSize: 30 }}
                      />
                    </div>
                    <div className="closedbutton">
                      <button className="GTWalsheimProBold text-white text-opacity-40 bg-brand_primary p-2 rounded-xl cursor-not-allowed">
                        Closed
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <Participants raffle={raffle} />
              )}
            </div>
          </div>
        </div>
        <div style={{ maxWidth: '800px' }}>
          <Footer />
        </div>
      </div>
    </div>
  );
};

interface IRaffleEndedDetailsProps {
  raffle: Raffle;
  updateRaffle: () => void;
}

const RaffleEndedScreenWithLayout: FC<IRaffleEndedDetailsProps> = (props) => {
  const { push } = useHistory();

  return (
    <Screen onBackNavigation={() => push('/')}>
      <RaffleEndedScreen {...props} />
    </Screen>
  );
};

export default RaffleEndedScreenWithLayout;

/*
{device === DeviceType.Phone ? (
        <div className="w-full">
          <h1 className="text-brand_smooth_gray text-center text-lg">
            {
              //@ts-ignore
              raffle.prizes[0].meta.name
            }
          </h1>
          <div className={classes.spacer} />
          <RaffleInfoSection
            raffle={raffle}
            userConnected={!!draffleClient.provider.wallet.publicKey}
            userTickets={entrant?.tickets}
          />
          <div className={classes.spacer} />
          <div className={classes.actionSectionContainer + ' border p-6 '}>
            <div className={classes.actionSection}>
              <EndedRaffleActionSection
                raffle={raffle}
                winningTickets={winningTickets}
                userPubkey={draffleClient.provider.wallet.publicKey}
                entrant={entrant}
                entrantWinningTickets={entrantWinningTickets}
                scrollRef={prizeGalleryRef}
                publicKey={publicKey}
                claimPrize={claimPrize}
              />
            </div>
          </div>
          <div className={classes.spacer} />
          <Typography variant="overline">Results</Typography>
          <PrizeGalleryEnded
            raffle={raffle}
            entrantWinningTickets={entrantWinningTickets}
            winningTickets={winningTickets}
            claimPrize={claimPrize}
            scrollRef={prizeGalleryRef}
            publicKey={publicKey}
          />
          <div className={classes.spacer} />
        </div>
      ) : (
        <div className="w-full">
          <div className={classes.mainContent}>
            <div className={classes.prizesSection}>
              <PrizeGalleryEnded
                raffle={raffle}
                entrantWinningTickets={entrantWinningTickets}
                winningTickets={winningTickets}
                claimPrize={claimPrize}
                scrollRef={prizeGalleryRef}
                publicKey={publicKey}
              />
            </div>
            <div
              className={
                classes.detailsSection +
                ' bg-brand_dark_gray bg-opacity-80 px-6 py-6'
              }
            >
              <div className="w-full">
                <p className="text-2xl font-bold tracking-widest primary_font uppercase text-brand_secondary">
                  {
                    //@ts-ignore
                    raffle.prizes[0].meta.name
                  }
                </p>
              </div>
              <div className="buttons flex space-x-2 pt-3 pb-3">
                <button
                  onClick={() => setShowParticipants(false)}
                  className={`text-center border border-brand_secondary px-4 py-2 upperacse primary_font ${
                    showParticipants
                      ? 'bg-primary text-brand_secondary'
                      : 'bg-brand_accent text-brand_primary'
                  } tracking-wider border`}
                >
                  DETAILS
                </button>
                <button
                  onClick={() => setShowParticipants(true)}
                  className={`text-center px-4 py-2 uppercase border border-brand_secondary primary_font ${
                    !showParticipants
                      ? 'bg-primary text-brand_secondary'
                      : 'bg-brand_accent text-brand_primary'
                  } tracking-wider border`}
                >
                  PARTICIPANTS
                </button>
              </div>
              {/* <hr className="mt-6 bg-brand_secondary border-brand_secondary" /> */

//         {!showParticipants ? (
//           <div className="w-full">
//             <RaffleInfoSection
//               raffle={raffle}
//               userConnected={!!draffleClient.provider.wallet.publicKey}
//               userTickets={entrant?.tickets}
//             />
//             {/* <hr className="mb-6 bg-brand_secondary border-brand_secondary" /> */}
//             <div className={classes.actionSectionContainer}>
//               <div className={classes.actionSection}>
//                 <EndedRaffleActionSection
//                   raffle={raffle}
//                   winningTickets={winningTickets}
//                   userPubkey={draffleClient.provider.wallet.publicKey}
//                   entrant={entrant}
//                   entrantWinningTickets={entrantWinningTickets}
//                   scrollRef={prizeGalleryRef}
//                   publicKey={publicKey}
//                   claimPrize={claimPrize}
//                 />
//               </div>
//             </div>
//           </div>
//         ) : (
//           <Participants raffle={raffle} />
//         )}
//       </div>
//     </div>
//   </div>
// )}
